import { useQuery } from "@tanstack/react-query";
import { useAppContext } from "contexts/AppContext";
import { FeatureFlag } from "helpers/types";
import { TimeFrequencyMs } from "types/time-frequency";

const STORAGE_KEY = "feature-flags";
const REQUEST_TIMEOUT_MS = 5000;

function readStoredFlags(): FeatureFlag[] | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return undefined;
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function storeFlags(flags: FeatureFlag[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
  } catch {
    // ignore
  }
}

export default function useFeatureFlags() {
  const { apiService } = useAppContext();
  return useQuery<FeatureFlag[]>({
    queryKey: ["feature-flags"],
    queryFn: async () => {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const response = await apiService.request("/feature", {
          method: "GET",
          signal: controller.signal,
        });
        const data = await response?.json();
        if (!response?.ok || !Array.isArray(data)) {
          throw new Error("Feature flags request failed");
        }
        storeFlags(data);
        return data;
      } finally {
        window.clearTimeout(timer);
      }
    },
    initialData: readStoredFlags,
    initialDataUpdatedAt: 0,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: TimeFrequencyMs.Hour,
  });
}
